import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, ShoppingBag, Plus, MessageCircle, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { useUnreadCount } from '@/hooks/useUnreadCount';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { Badge } from './ui/badge';
import { cn } from '@/lib/utils';

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadCount: messagesUnread } = useUnreadCount();
  const { unreadCount: notificationsUnread } = useUnreadNotifications();
  const unreadCount = messagesUnread + notificationsUnread;
  
  // Swipe state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 80;

  const tabs = [
    { id: 'explore', label: 'Explore', icon: Compass, path: '/' },
    { id: 'shop', label: 'Market', icon: ShoppingBag, path: '/marketplace' },
    { id: 'create', label: '', icon: Plus, path: user ? '/mobile-asset-selector' : '/auth?redirect=/mobile-asset-selector', isCenter: true },
    { id: 'messages', label: 'Messages', icon: MessageCircle, path: user ? '/messages' : '/auth?redirect=/messages' },
    { id: 'profile', label: 'Profile', icon: User, path: user ? '/profile' : '/auth?redirect=/profile' },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/engines') || path.startsWith('/parts-tools') || path.startsWith('/jobs') || path.startsWith('/services')) return 'explore';
    if (path.startsWith('/marketplace')) return 'shop';
    if (path.startsWith('/mobile-create') || path.startsWith('/create-listing') || path === '/mobile-asset-selector') return 'create';
    if (path.startsWith('/messages')) return 'messages';
    if (path.startsWith('/profile') || path.startsWith('/auth') || path.startsWith('/pro') || path.startsWith('/reset-password')) return 'profile';
    return null;
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab: typeof tabs[0]) => {
    navigate(tab.path);
  };

  // Swipe handlers — must run before any early return to keep hook order stable
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Navigate between tabs (skip center "create" tab for swipe)
    const navigableTabs = tabs.filter(t => !t.isCenter);
    const currentNavIndex = navigableTabs.findIndex(t => t.id === activeTab);

    if (isLeftSwipe && currentNavIndex < navigableTabs.length - 1) {
      navigate(navigableTabs[currentNavIndex + 1].path);
    } else if (isRightSwipe && currentNavIndex > 0) {
      navigate(navigableTabs[currentNavIndex - 1].path);
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, activeTab, navigate, tabs]);

  // Hide on news article reader for a clean reading experience
  if (location.pathname.startsWith('/news/')) {
    return null;
  }

  // Check if we can swipe in either direction
  const navigableTabs = tabs.filter(t => !t.isCenter);
  const currentNavIndex = navigableTabs.findIndex(t => t.id === activeTab);
  const canSwipeLeft = currentNavIndex < navigableTabs.length - 1;
  const canSwipeRight = currentNavIndex > 0;

  return (
    <nav 
      className={cn(
        "lg:hidden fixed bottom-0 left-0 right-0 z-50",
        "bg-background/80 backdrop-blur-xl",
        "border-t border-border/30",
        "shadow-[0_-8px_30px_rgba(0,0,0,0.12)]",
        "safe-area-bottom"
      )}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Swipe indicators */}
      {canSwipeRight && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none">
          <ChevronLeft className="w-4 h-4 text-muted-foreground animate-pulse" />
        </div>
      )}
      {canSwipeLeft && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none">
          <ChevronRight className="w-4 h-4 text-muted-foreground animate-pulse" />
        </div>
      )}
      
      <div className="flex items-center justify-around h-16 px-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === 'messages' && unreadCount > 0;
          const isCenter = 'isCenter' in tab && tab.isCenter;

          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 relative touch-target",
                "active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isCenter ? "flex-[0.8]" : "",
                isActive 
                  ? "text-primary" 
                  : "text-muted-foreground active:text-foreground"
              )}
              aria-label={tab.label || 'Create'}
              aria-current={isActive ? 'page' : undefined}
            >
              {isCenter ? (
                <div className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center -mt-6",
                  "bg-gradient-to-br from-primary to-primary/80",
                  "shadow-lg shadow-primary/30",
                  "transition-all duration-300",
                  "active:scale-95 active:shadow-primary/50"
                )}>
                  <Icon className="h-7 w-7 text-primary-foreground" />
                </div>
              ) : (
                <>
                  <div className="relative">
                    <div className={cn(
                      "p-1.5 rounded-xl transition-all duration-200",
                      isActive && "bg-primary/10"
                    )}>
                      <Icon className={cn(
                        "h-5 w-5 transition-all duration-200",
                        isActive && "scale-110"
                      )} />
                    </div>
                    {showBadge && (
                      <Badge 
                        variant="destructive" 
                        className={cn(
                          "absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center p-0 text-[10px]",
                          "animate-in zoom-in-50 duration-200"
                        )}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] font-medium transition-all mt-0.5",
                    isActive ? "font-semibold text-primary" : "text-muted-foreground"
                  )}>
                    {tab.label}
                  </span>
                  {/* Active indicator line */}
                  <div className={cn(
                    "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300",
                    isActive 
                      ? "w-6 bg-primary opacity-100" 
                      : "w-0 bg-transparent opacity-0"
                  )} />
                </>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
